import { createFileRoute } from "@tanstack/react-router";
import { useSiteData } from "@/lib/use-site-data";

export const Route = createFileRoute("/legal/condiciones")({
  head: () => ({
    meta: [
      { title: "Condiciones de reserva · El Pajar de Benito" },
      { name: "description", content: "Condiciones generales de contratación de reservas en El Pajar de Benito: precios, pago, cancelación y check-in." },
      { property: "og:title", content: "Condiciones de reserva · El Pajar de Benito" },
      { property: "og:description", content: "Condiciones generales de contratación aplicables a las reservas realizadas en elpajardebenito.es." },
      { property: "og:url", content: "https://elpajardebenito.es/legal/condiciones" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: "https://elpajardebenito.es/legal/condiciones" }],
  }),
  component: Condiciones,
});

function Condiciones() {
  const { config } = useSiteData();
  const hoy = new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" });
  const titular = config.nombre_fiscal || "CASA JUANA Y LUCIO S.L.";
  return (
    <>
      <h1>Condiciones generales de contratación</h1>
      <p className="text-sm text-muted-foreground">Última actualización: {hoy}</p>

      <h2>1. Partes</h2>
      <p>Las presentes condiciones regulan la contratación del servicio de alojamiento turístico ofrecido por <strong>{titular}</strong>, con CIF {config.nif || "B85745362"} y domicilio en {config.direccion}, {config.codigo_postal} {config.ciudad}, {config.pais} (en adelante, "el Alojamiento"), a través del sitio web elpajardebenito.es. La contratación implica la aceptación expresa de estas condiciones por parte del cliente.</p>

      <h2>2. Objeto</h2>
      <p>El servicio contratado consiste en el alquiler del alojamiento rural "{config.nombre_comercial}" para uso turístico, en las fechas y con las características que el cliente selecciona durante el proceso de reserva.</p>

      <h2>3. Proceso de reserva</h2>
      <ol>
        <li>El cliente selecciona fechas de entrada y salida y comprueba disponibilidad.</li>
        <li>Elige el alojamiento y confirma el precio total, que incluye los impuestos aplicables.</li>
        <li>Cumplimenta sus datos personales y de contacto.</li>
        <li>Acepta expresamente estas condiciones y la política de privacidad.</li>
        <li>Realiza el pago a través de la pasarela segura de Redsys.</li>
        <li>Recibe por email la confirmación de la reserva.</li>
      </ol>
      <p>La reserva quedará perfeccionada únicamente tras la confirmación del pago por parte de la entidad bancaria.</p>

      <h2>4. Precio y forma de pago</h2>
      <p>Los precios mostrados en el sitio web incluyen el IVA aplicable. El pago se realiza íntegramente en el momento de la reserva mediante tarjeta de crédito o débito, a través de la pasarela de pago segura de Redsys (Servicios de Procesamiento, S.L.). El Alojamiento no almacena ni tiene acceso a los datos de la tarjeta.</p>

      <h2>5. Check-in y check-out</h2>
      <ul>
        <li><strong>Entrada (check-in):</strong> a partir de las {config.hora_checkin.slice(0,5)} h.</li>
        <li><strong>Salida (check-out):</strong> antes de las {config.hora_checkout.slice(0,5)} h.</li>
      </ul>
      <p>Cualquier variación deberá ser comunicada y acordada previamente con el Alojamiento.</p>

      <h2>6. Registro de viajeros</h2>
      <p>De acuerdo con la normativa vigente en materia de seguridad ciudadana, todos los huéspedes mayores de 14 años deberán facilitar sus datos identificativos y de documento (DNI, NIE o pasaporte) a la llegada al alojamiento. Estos datos serán comunicados a las autoridades competentes conforme a la legislación aplicable.</p>

      <h2>7. Política de cancelación</h2>
      {config.politica_cancelacion ? (
        <p style={{ whiteSpace: "pre-line" }}>{config.politica_cancelacion}</p>
      ) : (
        <>
          <ul>
            <li><strong>Cancelaciones con más de 30 días</strong> de antelación al check-in: reembolso del 100% del importe abonado.</li>
            <li><strong>Entre 30 y 15 días</strong> de antelación: reembolso del 50%.</li>
            <li><strong>Menos de 15 días</strong> de antelación o no presentación: sin derecho a reembolso.</li>
          </ul>
          <p>Las cancelaciones deberán solicitarse por escrito al email <a href={`mailto:${config.email_contacto}`}>{config.email_contacto}</a>.</p>
        </>
      )}

      <h2>8. Derecho de desistimiento</h2>
      <p>Conforme al artículo 103.l) del Real Decreto Legislativo 1/2007, de 16 de noviembre, por el que se aprueba el texto refundido de la Ley General para la Defensa de los Consumidores y Usuarios (TRLGDCU), <strong>los servicios de alojamiento para fines distintos del de servir de vivienda cuando el contrato prevea una fecha o un período de ejecución específicos quedan excluidos del derecho de desistimiento</strong> propio de la contratación a distancia. En consecuencia, la reserva realizada se rige por la política de cancelación descrita en el punto anterior.</p>

      <h2>9. Obligaciones del cliente</h2>
      <ul>
        <li>Utilizar el alojamiento y sus instalaciones conforme a su destino, con la diligencia debida.</li>
        <li>Respetar el número máximo de personas contratado. No se permitirá el acceso de personas adicionales sin autorización previa.</li>
        <li>Respetar las normas de convivencia, en particular el descanso nocturno.</li>
        <li>Comunicar cualquier desperfecto o incidencia detectada.</li>
        <li>Responder de los daños causados por dolo o negligencia durante la estancia.</li>
      </ul>

      <h2>10. Responsabilidad</h2>
      <p>El Alojamiento no se hace responsable de la pérdida, hurto o robo de objetos personales del cliente durante su estancia, salvo que exista dolo o culpa grave del establecimiento. Se recomienda contratar un seguro de viaje.</p>

      <h2>11. Reclamaciones y resolución de litigios</h2>
      <p>El Alojamiento dispone de hojas de reclamaciones oficiales a disposición del cliente. Cualquier queja podrá dirigirse a <a href={`mailto:${config.email_contacto}`}>{config.email_contacto}</a>.</p>
      <p>Conforme al Reglamento (UE) 524/2013, se informa de la existencia de la plataforma europea de resolución de litigios en línea, accesible en <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener">https://ec.europa.eu/consumers/odr</a>.</p>

      <h2>12. Ley aplicable y jurisdicción</h2>
      <p>Las presentes condiciones se rigen por la legislación española. Para la resolución de cualquier controversia, las partes se someten a los Juzgados y Tribunales del domicilio del consumidor.</p>

      {config.politica_cancelacion && config.politica_cancelacion.length > 0 ? null : null}
    </>
  );
}